import { useState } from 'react';
import axios from 'axios';

// Helper function to generate valid MongoDB ObjectID-like strings for temporary IDs
const generateTempId = () => {
  return Array(24).fill(0).map(() => 
    Math.floor(Math.random() * 16).toString(16)
  ).join('');
};

export const useFileUpload = ({ chatId, setMessages, navigate, setError, setIsLoading, sseStreamingActive, currentUser }) => {
  const [uploading, setUploading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false); // Track processing state

  const handleFileUpload = async (formData) => {
    console.log("Starting file upload process...");
    
    // Prevent duplicate uploads
    if (uploading || isProcessing) {
      console.log('Upload already in progress, skipping...');
      return false;
    }
    
    // Log formData contents for debugging
    for (let pair of formData.entries()) {
      console.log(`FormData contains: ${pair[0]}:`, typeof pair[1] === 'object' ? pair[1].name : pair[1]);
    }

    try {
      setUploading(true);
      setIsProcessing(true);
      setError(null);
      setIsLoading(true);
      
      const token = localStorage.getItem('token');
      console.log(`Chat ID: ${chatId}, Token available: ${!!token}`);
      
      // If no chatId exists, we need to create a chat first
      let targetChatId = chatId;
      let newChatCreated = false;
      
      if (!targetChatId) {
        console.log('No existing chat, creating a new one first');
        try {
          // Create a new chat
          const createResponse = await axios.post(
            `${process.env.REACT_APP_API_URL}/chat`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          if (!createResponse.data.chat?._id) {
            throw new Error('Failed to create new chat');
          }
          
          targetChatId = createResponse.data.chat._id;
          newChatCreated = true;
          console.log('Created new chat with ID:', targetChatId);
          
          // Navigate to the new chat
          navigate(`/chat/${targetChatId}`);
        } catch (error) {
          console.error('Error creating new chat:', error);
          throw new Error('Failed to create chat for file upload');
        }
      }
      
      // Add chatId to form data
      formData.append('chatId', targetChatId);
      
      // 🧪 Check for test user impersonation (same as in useMessageStreaming)
      const activeTestUserId = localStorage.getItem('devActiveTestUserId');
      const useTestUser = activeTestUserId && localStorage.getItem('devTestOrgMode') === 'true';
      
      if (useTestUser) {
        formData.append('testUserId', activeTestUserId);
      }
      
      // 🧪 Prepare headers with test org override if in test mode
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'multipart/form-data',
      };
      
      if (useTestUser) {
        headers['X-Test-Org-Id'] = localStorage.getItem('devTestOrgId');
        headers['X-Test-User-Id'] = activeTestUserId;
        console.log('🧪 Adding test org header to file upload:', headers['X-Test-Org-Id']);
        console.log('🧪 Adding test user header to file upload:', headers['X-Test-User-Id']);
      }
      
      // Upload files and handle streaming response
      console.log('Uploading files and handling streaming response...');
      const uploadResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/upload`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            ...(useTestUser && {
              'X-Test-Org-Id': localStorage.getItem('devTestOrgId'),
              'X-Test-User-Id': activeTestUserId
            })
          },
          body: formData
        }
      );
      
      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text();
        console.error('❌ File upload failed:', uploadResponse.status, errorText);
        throw new Error(`File upload failed: ${uploadResponse.status}`);
      }
      
      // Check if this is a streaming response (SSE)
      const contentType = uploadResponse.headers.get('content-type');
      if (contentType && contentType.includes('text/event-stream')) {
        console.log('📡 Detected SSE streaming response from file upload');
        
        // Handle SSE streaming response similar to useMessageStreaming
        sseStreamingActive.current = true;
        const reader = uploadResponse.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let botMessageId = null;
        let fullResponse = '';
        
        try {
          while (true) {
            const { done, value } = await reader.read();
            
            if (done) {
              console.log('File upload streaming completed');
              sseStreamingActive.current = false;
              setIsLoading(false);
              break;
            }
            
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n\n');
            buffer = lines.pop() || '';
            
            // Process each complete event
            for (const line of lines) {
              if (!line.trim()) continue;
              
              const eventMatch = line.match(/^event: (.+)$/m);
              const dataMatch = line.match(/^data: (.+)$/m);
              
              if (!dataMatch) continue;
              
              const eventType = eventMatch ? eventMatch[1] : 'message';
              let data;
              try {
                data = JSON.parse(dataMatch[1]);
              } catch (e) {
                console.warn('Error parsing SSE data:', e, dataMatch[1]);
                continue;
              }
              
              if (eventType === 'connect') {
                console.log('SSE connection established for file upload');
              } else if (eventType === 'status') {
                console.log('📄 Document status:', data.message);
                
                // Customize status message based on context
                let statusMessage = data.message || 'Processing document...';
                if (data.status === 'analyzing') {
                  // Check if hyper accuracy is enabled from formData
                  const hyperAccuracy = formData.get('hyperAccuracy') === 'true';
                  statusMessage = hyperAccuracy ? 'Analyzing with hyper accuracy' : 'Analyzing';
                } else if (data.status === 'processing') {
                  statusMessage = 'Processing document content...';
                }
                
                // Update messages with document status
                setMessages(prev => {
                  const currentMessages = Array.isArray(prev) ? prev : [];
                  
                  // Find or create bot message for status updates
                  let lastBotIndex = -1;
                  for (let i = currentMessages.length - 1; i >= 0; i--) {
                    if (currentMessages[i].sender === 'bot' && 
                        (currentMessages[i].isTemporary || currentMessages[i].isThinking || 
                         currentMessages[i].isAnalyzing)) {
                      lastBotIndex = i;
                      break;
                    }
                  }
                  
                  if (lastBotIndex === -1) {
                    // Create new bot message with analysis status
                    const newBotMessage = {
                      _id: `temp-analysis-${Date.now()}`,
                      text: '',
                      sender: 'bot',
                      timestamp: new Date(),
                      isAnalyzing: data.status === 'analyzing' || data.status === 'processing',
                      analysisStatus: statusMessage,
                      analysisProgress: data.progress || 0,
                      isThinking: false,
                      isTemporary: true
                    };
                    
                    return [...currentMessages, newBotMessage];
                  } else {
                    // Update existing bot message
                    const newMessages = [...currentMessages];
                    newMessages[lastBotIndex] = {
                      ...newMessages[lastBotIndex],
                      isAnalyzing: data.status === 'analyzing' || data.status === 'processing',
                      analysisStatus: statusMessage,
                      analysisProgress: data.progress || 0,
                      isThinking: false
                    };
                    
                    return newMessages;
                  }
                });
              } else if (eventType === 'message' || !eventType) {
                // Handle content chunks
                if (data.content) {
                  fullResponse += data.content;
                  console.log('📝 Processing content chunk from file upload:', {
                    contentLength: data.content.length,
                    fullResponseLength: fullResponse.length
                  });
                  
                  // Set up a timer to show feedback buttons if no more content comes
                  if (botMessageId) {
                    // Clear any existing timer
                    if (window.contentStreamingTimer) {
                      clearTimeout(window.contentStreamingTimer);
                    }
                    
                    // Set new timer to detect end of content streaming
                    window.contentStreamingTimer = setTimeout(() => {
                      console.log('📝 File upload content streaming appears to be complete');
                      setMessages(prev => prev.map(msg => 
                        msg._id === botMessageId ? {
                          ...msg,
                          isContentStreamingComplete: true
                        } : msg
                      ));
                    }, 1000); // Wait 1 second after last content chunk
                  }
                  
                  setMessages(prev => {
                    const currentMessages = Array.isArray(prev) ? prev : [];
                    
                    if (!botMessageId) {
                      // Create new bot message
                      const newBotMessageId = `temp-streaming-${Date.now()}`;
                      botMessageId = newBotMessageId;
                      
                      return [...currentMessages, {
                        _id: newBotMessageId,
                        text: data.content,
                        sender: 'bot',
                        timestamp: new Date(),
                        isStreaming: true,
                        isAnalyzing: false,
                        isThinking: false,
                        isTemporary: false
                      }];
                    } else {
                      // Update existing message
                      return currentMessages.map(msg =>
                        msg._id === botMessageId ? {
                          ...msg,
                          text: (msg.text || '') + data.content,
                          isStreaming: true,
                          isAnalyzing: false
                        } : msg
                      );
                    }
                  });
                }
              } else if (eventType === 'done') {
                console.log('File upload streaming done');
                
                // Clear the fallback timer since processing is done
                if (window.contentStreamingTimer) {
                  clearTimeout(window.contentStreamingTimer);
                  window.contentStreamingTimer = null;
                }
                
                // Mark message as complete
                setMessages(prev => {
                  return prev.map(msg =>
                    msg._id === botMessageId ? {
                      ...msg,
                      isStreaming: false,
                      isAnalyzing: false,
                      isThinking: false,
                      isTemporary: false,
                      isContentStreamingComplete: true
                    } : msg
                  );
                });
                
                sseStreamingActive.current = false;
                setIsLoading(false);
              } else if (eventType === 'error') {
                console.error('SSE error from file upload:', data.error);
                setError(data.error);
                sseStreamingActive.current = false;
                setIsLoading(false);
                
                // Clear the fallback timer on error
                if (window.contentStreamingTimer) {
                  clearTimeout(window.contentStreamingTimer);
                  window.contentStreamingTimer = null;
                }
              }
            }
          }
        } catch (streamError) {
          console.error('Error reading stream from file upload:', streamError);
          setError('Error reading response stream');
          sseStreamingActive.current = false;
          setIsLoading(false);
          
          // Clear the fallback timer on error
          if (window.contentStreamingTimer) {
            clearTimeout(window.contentStreamingTimer);
            window.contentStreamingTimer = null;
          }
        }
      } else {
        // Handle non-streaming response (fallback)
        console.log('📄 Non-streaming response from file upload');
        const response = await uploadResponse.json();
        
        console.log('Upload successful:', response);
        
        // Check if this was a duplicate
        if (response.isDuplicate) {
          console.log('Duplicate upload detected, skipping refresh');
          return true;
        }
        
        // Refresh messages for the current chat if setMessages is available
        if (setMessages && targetChatId) {
          console.log('Refreshing messages after upload...');
          try {
            const messagesResponse = await axios.get(
              `${process.env.REACT_APP_API_URL}/chat/${targetChatId}/messages`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            
            if (messagesResponse.data.messages) {
              setMessages(messagesResponse.data.messages);
              console.log('Messages refreshed after upload');
            }
          } catch (refreshError) {
            console.error('Error refreshing messages:', refreshError);
            // Don't fail the upload if refresh fails
          }
        }
      }
      
      // If we created a new chat, refresh the sidebar
      if (newChatCreated && typeof window !== 'undefined') {
        // Dispatch a custom event to refresh the sidebar
        window.dispatchEvent(new CustomEvent('refreshChats'));
      }
      
      return true; // Success
      
    } catch (error) {
      console.error('Upload error:', error);
      setError(error.message || 'Failed to upload files');
      return false;
    } finally {
      setUploading(false);
      setIsProcessing(false);
    }
  };

  return {
    handleFileUpload,
    uploading,
    isProcessing
  };
}; 